import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "input", "scope", "results", "viewAllLink"]
  static values = {
    globalPath: String,
    wikiPath: String,
    wikiId: String,
    wikiTitle: String,
  }

  connect() {
    this._selectedIndex = -1
    this._items = []
    this._pendingFetch = null
    this._lastRequestId = 0

    this._handleGlobalKeydown = this._handleGlobalKeydown.bind(this)
    window.addEventListener("keydown", this._handleGlobalKeydown)

    this._handleDocumentClick = this._handleDocumentClick.bind(this)
    document.addEventListener("click", this._handleDocumentClick, true)

    this._initializeScope()
    this._renderPlaceholder()
  }

  disconnect() {
    window.removeEventListener("keydown", this._handleGlobalKeydown)
    document.removeEventListener("click", this._handleDocumentClick, true)
    this._clearPendingFetch()
  }

  open() {
    queueMicrotask(() => {
      this.inputTarget.focus()
      this.inputTarget.select()
      this._renderPlaceholder()
      const q = this.inputTarget.value.toString().trim()
      if (q) {
        this._fetchNow()
      } else {
        this._closePanel()
      }
    })
  }

  close() {
    this._closePanel()
  }

  onFocus() {
    this._renderPlaceholder()

    const q = this.inputTarget.value.toString().trim()
    if (q) {
      this._fetchNow()
    } else {
      this._closePanel()
    }
  }

  onInput() {
    const q = this.inputTarget.value.toString().trim()
    if (!q) {
      this._renderEmpty()
      this._closePanel()
      return
    }

    this._openPanel()
    this._debounceFetch()
  }

  onScopeChange() {
    this._renderPlaceholder()
    this._fetchNow()
  }

  onKeydown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      this._openPanel()
      this._moveSelection(1)
      return
    }

    if (e.key === "ArrowUp") {
      e.preventDefault()
      this._openPanel()
      this._moveSelection(-1)
      return
    }

    if (e.key === "Enter") {
      e.preventDefault()
      const chosen = this._items[this._selectedIndex]
      if (chosen && chosen.href) {
        window.location.href = chosen.href
      } else {
        this._goToSearchPage()
      }
      return
    }

    if (e.key === "Escape") {
      this._closePanel()
    }
  }

  _handleGlobalKeydown(e) {
    // Cmd+K (Mac) / Ctrl+K (Windows/Linux)
    const isK = e.key?.toLowerCase?.() === "k"
    if ((e.metaKey || e.ctrlKey) && isK) {
      e.preventDefault()
      this.open()
      return
    }

    // Optional: "/" opens search when not typing in an input/editor.
    if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const el = e.target
      const isTyping =
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable ||
          el.closest?.(".milkdown"))

      if (!isTyping) {
        e.preventDefault()
        this.open()
      }
    }
  }

  _handleDocumentClick(e) {
    // Since this controller is attached to <body>, we need a more specific check for
    // clicks outside the navbar search UI.
    const withinPanel = this.panelTarget?.contains?.(e.target)
    const withinInput = this.inputTarget?.contains?.(e.target)
    const withinScope = this.hasScopeTarget && this.scopeTarget?.contains?.(e.target)
    if (withinPanel || withinInput || withinScope) return

    // Also treat clicks on the search input-group chrome as "inside".
    const root = this.inputTarget?.closest?.(".navbar-search")
    if (root && root.contains(e.target)) return

    this._closePanel()
  }

  _initializeScope() {
    const hasWiki = this._wikiId()
    const desired = hasWiki ? "wiki" : "all"

    if (this.hasScopeTarget) {
      this.scopeTarget.value = desired
    }
  }

  _renderPlaceholder() {
    const hasWiki = this._wikiId()
    const scope = this._scopeValue()

    if (scope === "wiki" && hasWiki) {
      const wikiTitle = (this.wikiTitleValue || "").toString().trim()
      this.inputTarget.placeholder = wikiTitle ? `Search ${wikiTitle}…` : "Search this wiki…"
    } else {
      this.inputTarget.placeholder = "Search all wikis…"
    }
  }

  _scopeValue() {
    return this.hasScopeTarget ? this.scopeTarget.value : "all"
  }

  _wikiId() {
    const v = (this.wikiIdValue || "").toString().trim()
    return v.length ? v : null
  }

  _clearPendingFetch() {
    if (this._pendingFetch) {
      clearTimeout(this._pendingFetch)
      this._pendingFetch = null
    }
  }

  _debounceFetch() {
    this._clearPendingFetch()
    this._pendingFetch = setTimeout(() => {
      this._fetchNow()
    }, 150)
  }

  async _fetchNow() {
    const q = this.inputTarget.value.toString()
    const trimmed = q.trim()

    if (!trimmed) {
      this._renderEmpty()
      this._closePanel()
      return
    }

    const requestId = ++this._lastRequestId
    const scope = this._scopeValue()
    const wikiId = this._wikiId()

    const url = new URL(this.globalPathValue.toString(), window.location.origin)
    url.pathname = `${url.pathname}.json`
    url.searchParams.set("q", trimmed)

    if (scope === "wiki" && wikiId) {
      url.searchParams.set("scope", "wiki")
      url.searchParams.set("wiki_id", wikiId)
    } else {
      url.searchParams.set("scope", "all")
    }

    // Keep the "View all" link in sync.
    this._syncViewAllLink(trimmed, scope, wikiId)

    const res = await fetch(url.toString(), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })

    if (!res.ok) {
      this._renderError()
      return
    }

    // If a newer request started after this one, ignore this response.
    if (requestId !== this._lastRequestId) return

    const data = await res.json()
    this._renderResults(data?.results)
  }

  _syncViewAllLink(q, scope, wikiId) {
    const url = new URL(this.globalPathValue.toString(), window.location.origin)
    url.searchParams.set("q", q)

    if (scope === "wiki" && wikiId && this.wikiPathValue) {
      const wikiUrl = new URL(this.wikiPathValue.toString(), window.location.origin)
      wikiUrl.searchParams.set("q", q)
      wikiUrl.searchParams.set("scope", "wiki")
      this.viewAllLinkTarget.href = wikiUrl.pathname + wikiUrl.search
      return
    }

    url.searchParams.set("scope", "all")
    this.viewAllLinkTarget.href = url.pathname + url.search
  }

  _goToSearchPage() {
    const q = this.inputTarget.value.toString().trim()
    if (!q) return

    const scope = this._scopeValue()
    const wikiId = this._wikiId()

    // Prefer the nicer wiki-scoped route when we're in a wiki.
    if (scope === "wiki" && wikiId && this.wikiPathValue) {
      const wikiUrl = new URL(this.wikiPathValue.toString(), window.location.origin)
      wikiUrl.searchParams.set("q", q)
      wikiUrl.searchParams.set("scope", "wiki")
      window.location.href = wikiUrl.pathname + wikiUrl.search
      return
    }

    const url = new URL(this.globalPathValue.toString(), window.location.origin)
    url.searchParams.set("q", q)
    url.searchParams.set("scope", "all")
    window.location.href = url.pathname + url.search
  }

  _renderEmpty() {
    this.resultsTarget.innerHTML = ""
    this._items = []
    this._selectedIndex = -1
  }

  _renderError() {
    this.resultsTarget.innerHTML = ""
    const div = document.createElement("div")
    div.className = "list-group-item text-body-secondary"
    div.textContent = "Search failed. Try again."
    this.resultsTarget.appendChild(div)
    this._items = []
    this._selectedIndex = -1
    this._openPanel()
  }

  _renderResults(results) {
    this.resultsTarget.innerHTML = ""

    const pages = Array.isArray(results?.pages) ? results.pages : []
    const tags = Array.isArray(results?.tags) ? results.tags : []
    const wikis = Array.isArray(results?.wikis) ? results.wikis : []
    const users = Array.isArray(results?.users) ? results.users : []

    this._items = []
    this._selectedIndex = -1

    if (!pages.length && !tags.length && !wikis.length && !users.length) {
      const div = document.createElement("div")
      div.className = "list-group-item text-body-secondary"
      div.textContent = "No results."
      this.resultsTarget.appendChild(div)
      this._openPanel()
      return
    }

    if (pages.length) {
      this._appendSection("Pages")
      pages.forEach((p) => this._appendResultItem(p))
    }

    if (tags.length) {
      this._appendSection("Tags")
      tags.forEach((t) => this._appendResultItem(t))
    }

    if (wikis.length) {
      this._appendSection("Wikis")
      wikis.forEach((w) => this._appendResultItem(w))
    }

    if (users.length) {
      this._appendSection("Users")
      users.forEach((u) => this._appendResultItem(u))
    }

    this._moveSelection(1)
    this._openPanel()
  }

  _appendSection(title) {
    const div = document.createElement("div")
    div.className = "list-group-item small text-body-secondary"
    div.textContent = title
    this.resultsTarget.appendChild(div)
  }

  _appendResultItem(item) {
    const a = document.createElement("a")
    a.className = "list-group-item list-group-item-action d-flex justify-content-between gap-2"
    a.href = item.url

    const q = this.inputTarget.value.toString().trim().toLowerCase()
    const name = (item.name || "").toString()
    const nameLower = name.toLowerCase()
    const titleMatch = q && nameLower.includes(q)

    const left = document.createElement("div")
    left.className = "d-flex flex-column gap-1 flex-grow-1"
    left.style.minWidth = "0"

    const titleRow = document.createElement("div")
    titleRow.className = "d-flex align-items-center gap-2"
    titleRow.style.minWidth = "0"

    const iconEl = this._buildIconEl(item)
    if (iconEl) titleRow.appendChild(iconEl)

    const titleText = document.createElement("span")
    titleText.textContent = name
    titleRow.appendChild(titleText)

    left.appendChild(titleRow)

    const snippetHtml = (item.snippet_html || "").toString()
    const previewText = (item.preview_text || "").toString()
    const summaryText = (item.summary || "").toString()
    const shouldShowMatchedSnippet = item.type === "page" && snippetHtml && q && !titleMatch
    const shouldShowTitlePreview = item.type === "page" && titleMatch && previewText

    if (shouldShowMatchedSnippet) {
      const snippet = document.createElement("div")
      snippet.className = "small text-body-secondary text-truncate"
      snippet.innerHTML = this._sanitizeSnippetHtml(snippetHtml)
      left.appendChild(snippet)
    } else if (shouldShowTitlePreview) {
      const snippet = document.createElement("div")
      snippet.className = "small text-body-secondary text-truncate"
      snippet.textContent = previewText
      left.appendChild(snippet)
    } else if (item.type === "page" && summaryText.trim() !== "") {
      const snippet = document.createElement("div")
      snippet.className = "small text-body-secondary text-truncate"
      snippet.textContent = summaryText
      left.appendChild(snippet)
    }

    const right = document.createElement("span")
    right.className = "small text-body-secondary"
    right.textContent = item.wiki_title || ""

    a.appendChild(left)
    a.appendChild(right)

    this.resultsTarget.appendChild(a)
    this._items.push(a)
  }

  _openPanel() {
    if (this.hasPanelTarget) this.panelTarget.hidden = false
  }

  _closePanel() {
    if (!this.hasPanelTarget) return
    this.panelTarget.hidden = true
    this._items.forEach((el) => el.classList.remove("active"))
    this._selectedIndex = -1
  }

  _buildIconEl(item) {
    const kind = (item.icon_kind || "").toString()
    const colors = Array.isArray(item.icon_color_classes) ? item.icon_color_classes : []
    const extraColorClasses = colors.filter((c) => typeof c === "string" && c.trim()).join(" ")

    if (kind === "emoji" && item.icon_emoji) {
      const span = document.createElement("span")
      span.className = ["page-icon", "page-icon--xs", "page-icon--emoji", extraColorClasses].filter(Boolean).join(" ")
      span.setAttribute("aria-hidden", "true")
      span.textContent = item.icon_emoji.toString()
      return span
    }

    if (kind === "fa" && item.icon_fa_classes) {
      const span = document.createElement("span")
      span.className = ["page-icon", "page-icon--xs", "page-icon--fa", extraColorClasses].filter(Boolean).join(" ")
      const i = document.createElement("i")
      i.className = item.icon_fa_classes.toString()
      span.appendChild(i)
      return span
    }

    return null
  }

  _sanitizeSnippetHtml(html) {
    // Conservative sanitizer: allow only <mark> with no attributes.
    // Everything else is turned into plain text.
    const template = document.createElement("template")
    template.innerHTML = html.toString()

    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_ELEMENT, null)
    const toStrip = []

    while (walker.nextNode()) {
      const el = walker.currentNode
      if (el.tagName === "MARK") {
        // Remove any attributes, just in case.
        Array.from(el.attributes).forEach((a) => el.removeAttribute(a.name))
      } else {
        toStrip.push(el)
      }
    }

    toStrip.forEach((el) => {
      const text = document.createTextNode(el.textContent || "")
      el.replaceWith(text)
    })

    return template.innerHTML
  }

  _moveSelection(delta) {
    if (!this._items.length) return

    const next = (this._selectedIndex + delta + this._items.length) % this._items.length
    this._setSelectedIndex(next)
  }

  _setSelectedIndex(idx) {
    this._items.forEach((el) => el.classList.remove("active"))
    this._selectedIndex = idx
    const el = this._items[idx]
    if (!el) return

    el.classList.add("active")
    el.scrollIntoView({ block: "nearest" })
  }
}
